import { TreeSelect } from "antd";
import { useCallback, useMemo } from "react";
import { useOrganizationTree } from "@/modules/organizations/hooks/useOrganizations";
import type { OrgPickerTreeNode } from "@/modules/organizations/types";

interface OrgTreeSelectNode {
	key: string;
	value: string;
	title: string;
	disabled?: boolean;
	children?: OrgTreeSelectNode[];
}

function toSelectNode(node: OrgPickerTreeNode, excludeId?: string): OrgTreeSelectNode {
	return {
		key: node.id,
		value: node.id,
		title: node.name,
		disabled: excludeId ? node.id === excludeId : false,
		children:
			node.children.length > 0 ? node.children.map((c) => toSelectNode(c, excludeId)) : undefined,
	};
}

interface OrganizationTreeSelectProps {
	value?: string;
	onChange?: (value: string | undefined, name?: string) => void;
	placeholder?: string;
	disabled?: boolean;
	/** Org ID to disable in the tree (e.g. viewerId when selecting target) */
	excludeId?: string;
	status?: "error" | "warning";
}

export function OrganizationTreeSelect({
	value,
	onChange,
	placeholder = "Tashkilotni tanlang",
	disabled,
	excludeId,
	status,
}: OrganizationTreeSelectProps) {
	const { data: tree, isLoading, nodesById } = useOrganizationTree();

	const treeData = useMemo(
		() => (tree ?? []).map((node) => toSelectNode(node, excludeId)),
		[tree, excludeId]
	);

	const selectedName = value ? nodesById.get(value)?.name : undefined;
	const displayValue = value ? { value, label: selectedName ?? value } : undefined;

	const handleChange = useCallback(
		(selected: { value: string; label: React.ReactNode } | undefined) => {
			if (selected == null) {
				onChange?.(undefined);
				return;
			}
			const id = typeof selected === "string" ? selected : selected.value;
			const label =
				typeof selected === "string" ? undefined : ((selected.label as string) ?? undefined);
			onChange?.(id, label ?? nodesById.get(id)?.name);
		},
		[onChange, nodesById]
	);

	return (
		<TreeSelect
			labelInValue
			value={displayValue}
			onChange={handleChange}
			placeholder={placeholder}
			disabled={disabled}
			status={status}
			loading={isLoading}
			treeData={treeData}
			showSearch
			treeNodeFilterProp="title"
			allowClear
			style={{ width: "100%" }}
			styles={{ popup: { root: { maxHeight: 300 } } }}
		/>
	);
}
